var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { isValidElement, } from 'react';
import { DataTableBase, DataTableRenderContext, useCanAccess, useListContextWithProps, useResourceContext, useStore, } from 'ra-core';
import { Table, useThemeProps, } from '@mui/material';
import clsx from 'clsx';
import { BulkActionsToolbar } from '../BulkActionsToolbar';
import { BulkDeleteButton } from '../../button';
import { ListNoResults } from '../ListNoResults';
import { DataTableClasses, DataTableRoot } from './DataTableRoot';
import { DataTableLoading } from './DataTableLoading';
import { DataTableBody } from './DataTableBody';
import { DataTableHead } from './DataTableHead';
import { DataTableColumn } from './DataTableColumn';
import { DataTableNumberColumn } from './DataTableNumberColumn';
import { ColumnsSelector } from './ColumnsSelector';
import { DataTableRowSxContext } from './DataTableRowSxContext';
var DefaultFoot = function (_props) { return null; };
var PREFIX = 'RaDataTable';
/**
 * The DataTable component renders a list of records as a table.
 * It is usually used as a child of the <List> and <ReferenceManyField> components.
 *
 * Props:
 *  - body
 *  - bulkActionToolbar
 *  - bulkActionButtons
 *  - children
 *  - empty
 *  - expand
 *  - footer
 *  - header
 *  - hover
 *  - isRowExpandable
 *  - isRowSelectable
 *  - rowClick
 *  - rowSx
 *  - size
 *  - sx
 *
 * @example // Display all posts as a data table
 * const postRowSx = (record, index) => ({
 *     backgroundColor: record.nb_views >= 500 ? '#efe' : 'white',
 * });
 * export const PostList = () => (
 *     <List>
 *         <DataTable rowSx={postRowSx}>
 *             <DataTable.Col source="id" />
 *             <DataTable.Col source="title" />
 *             <DataTable.Col source="body" />
 *             <DataTable.Col>
 *                 <EditButton />
 *             </DataTable.Col>
 *         </DataTable>
 *     </List>
 * );
 *
 * @example // Display all the comments of the current post as a data table
 * <ReferenceManyField reference="comments" target="post_id">
 *     <DataTable>
 *         <DataTable.Col source="id" />
 *         <DataTable.Col source="body" />
 *         <DataTable.Col source="created_at" />
 *         <DataTable.Col>
 *             <EditButton />
 *         </DataTable.Col>
 *     </DataTable>
 * </ReferenceManyField>
 *
 * @example // Usage outside of a <List> or a <ReferenceManyField>.
 *
 * const sort = { field: 'published_at', order: 'DESC' };
 *
 * export const MyCustomList = (props) => {
 *     const { data, total, isPending } = useGetList(
 *         'posts',
 *         { pagination: { page: 1, perPage: 10 }, sort: sort }
 *     );
 *
 *     return (
 *         <DataTable
 *             data={data}
 *             total={total}
 *             isPending={isPending}
 *             sort={sort}
 *             selectedIds={[]}
 *             setSort={() => {
 *                 console.log('set sort');
 *             }}
 *             onSelect={() => {
 *                 console.log('on select');
 *             }}
 *             onToggleItem={() => {
 *                 console.log('on toggle item');
 *             }}
 *         >
 *             <DataTable.Col source="id" />
 *             <DataTable.Col source="title" />
 *         </DataTable>
 *     );
 * }
 */
export var DataTable = React.forwardRef(function DataTable(inProps, ref) {
    var props = useThemeProps({
        props: inProps,
        name: PREFIX,
    });
    var resourceFromContext = useResourceContext(props);
    var canDelete = useCanAccess({
        resource: resourceFromContext,
        action: 'delete',
    }).canAccess;
    var _a = props.body, TableBody = _a === void 0 ? DataTableBody : _a, _b = props.head, TableHead = _b === void 0 ? DataTableHead : _b, _c = props.foot, TableFoot = _c === void 0 ? DefaultFoot : _c, children = props.children, className = props.className, empty = props.empty, expand = props.expand, bulkActionsToolbar = props.bulkActionsToolbar, _d = props.bulkActionButtons, bulkActionButtons = _d === void 0 ? canDelete ? defaultBulkActionButtons : false : _d, rowSx = props.rowSx, _e = props.size, size = _e === void 0 ? 'small' : _e, sx = props.sx, rest = __rest(props, ["body", "head", "foot", "children", "className", "empty", "expand", "bulkActionsToolbar", "bulkActionButtons", "rowSx", "size", "sx"]);
    var onToggleItem = useListContextWithProps(props).onToggleItem;
    var hasBulkActions = !!bulkActionButtons !== false;
    var storeKey = props.storeKey || "".concat(resourceFromContext, ".datatable");
    var columnRanks = useStore("".concat(storeKey, "_columnRanks"))[0];
    var columns = columnRanks
        ? reorderChildren(children, columnRanks)
        : children;
    var loading = (React.createElement(DataTableLoading, { className: className, expand: expand, hasBulkActions: hasBulkActions, nbChildren: React.Children.count(children), size: size }));
    return (React.createElement(DataTableBase, __assign({}, props, { hasBulkActions: hasBulkActions, loading: loading, empty: empty === undefined ? (React.createElement(ListNoResults, { resource: resourceFromContext })) : (empty) }),
        React.createElement(DataTableRowSxContext.Provider, { value: rowSx },
            React.createElement(DataTableRoot, { sx: sx, className: clsx(DataTableClasses.root, className) },
                onToggleItem &&
                    (bulkActionsToolbar !== null && bulkActionsToolbar !== void 0 ? bulkActionsToolbar : (bulkActionButtons !== false && (React.createElement(BulkActionsToolbar, null, isValidElement(bulkActionButtons)
                        ? bulkActionButtons
                        : defaultBulkActionButtons)))),
                React.createElement("div", { className: DataTableClasses.tableWrapper },
                    React.createElement(Table, __assign({ ref: ref, className: DataTableClasses.table, size: size }, sanitizeRestProps(rest)),
                        React.createElement(DataTableRenderContext.Provider, { value: "header" },
                            React.createElement(TableHead, null, columns)),
                        React.createElement(TableBody, null, columns),
                        React.createElement(DataTableRenderContext.Provider, { value: "footer" },
                            React.createElement(TableFoot, null, columns)))))),
        React.createElement(DataTableRenderContext.Provider, { value: "columnsSelector" },
            React.createElement(ColumnsSelector, null, children))));
});
DataTable.Col = DataTableColumn;
DataTable.NumberCol = DataTableNumberColumn;
DataTable.displayName = 'DataTable';
var defaultBulkActionButtons = React.createElement(BulkDeleteButton, null);
/**
 * Reorder children based on columnRanks
 *
 * Note that columnRanks may be shorter than the number of children
 */
var reorderChildren = function (children, columnRanks) {
    return React.Children.toArray(children).reduce(function (acc, child, index) {
        var rank = columnRanks.indexOf(index);
        if (rank === -1) {
            // if the column is not in columnRanks, keep it at the same index
            acc[index] = child;
        }
        else {
            // if the column is in columnRanks, move it to the rank index
            acc[rank] = child;
        }
        return acc;
    }, []);
};
var sanitizeRestProps = function (_a) {
    var sort = _a.sort, data = _a.data, expandSingle = _a.expandSingle, hiddenColumns = _a.hiddenColumns, hover = _a.hover, isLoading = _a.isLoading, isPending = _a.isPending, isRowExpandable = _a.isRowExpandable, isRowSelectable = _a.isRowSelectable, onSelect = _a.onSelect, onToggleItem = _a.onToggleItem, resource = _a.resource, rowClick = _a.rowClick, setSort = _a.setSort, selectedIds = _a.selectedIds, storeKey = _a.storeKey, total = _a.total, rest = __rest(_a, ["sort", "data", "expandSingle", "hiddenColumns", "hover", "isLoading", "isPending", "isRowExpandable", "isRowSelectable", "onSelect", "onToggleItem", "resource", "rowClick", "setSort", "selectedIds", "storeKey", "total"]);
    return rest;
};
//# sourceMappingURL=DataTable.js.map